/** Extract stable diagnostic fields from unknown thrown values (domain-safe). */

export type ErrorSourceInfo = {
  sourceCode: string;
  sourceName: string;
  sourceMessage: string;
  sourceStack?: string;
};

export function describeErrorSource(error: unknown): ErrorSourceInfo {
  if (error == null) {
    return { sourceCode: '', sourceName: '', sourceMessage: '' };
  }
  if (typeof error === 'string') {
    return { sourceCode: '', sourceName: 'String', sourceMessage: error };
  }
  const record = error as {
    code?: unknown;
    name?: unknown;
    message?: unknown;
    stack?: unknown;
  };
  const sourceCode = typeof record.code === 'string' ? record.code : '';
  const sourceName = typeof record.name === 'string'
    ? record.name
    : error instanceof Error
      ? error.name
      : typeof error;
  const sourceMessage = typeof record.message === 'string'
    ? record.message
    : error instanceof Error
      ? error.message
      : String(error);
  const sourceStack = typeof record.stack === 'string'
    ? record.stack
    : error instanceof Error
      ? error.stack
      : undefined;
  return { sourceCode, sourceName, sourceMessage, sourceStack };
}

/**
 * True when `stack` looks like a captured call stack with frames, not just
 * "ErrorName: message" (common for FirebaseError / callable INTERNAL).
 */
export function isMeaningfulStackTrace(stack: string | undefined | null): boolean {
  if (typeof stack !== 'string') return false;
  const trimmed = stack.trim();
  if (!trimmed.includes('\n')) return false;
  // V8 / Chrome / Node / Edge: "at fn (file:line:col)" or "at file:line:col"
  if (/\n\s*at\s+\S+/.test(trimmed)) return true;
  // Firefox / Safari: "fn@file:line:col"
  if (/\n[^@\n]+@\S+:\d+/.test(trimmed)) return true;
  // Generic multi-line with file:line:col markers
  return /:\d+:\d+/.test(trimmed);
}

/** One-line operator-facing summary for audit / console (not end-user copy). */
export function buildErrorSummary(opts: {
  code: string;
  sourceCode?: string;
  sourceMessage?: string;
  feature?: string;
  operation?: string;
}): string {
  const parts = [opts.code];
  if (opts.sourceCode && opts.sourceCode !== opts.code) parts.push(opts.sourceCode);
  if (opts.feature) parts.push(`@${opts.feature}`);
  if (opts.operation) parts.push(`#${opts.operation}`);
  const msg = String(opts.sourceMessage ?? '').trim();
  if (msg && msg !== opts.code && msg !== opts.sourceCode) {
    parts.push(`— ${msg.slice(0, 160)}`);
  }
  return parts.join(' ');
}
